import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import moment from 'moment';
import { paramsToFormData } from 'utils/utils';
import { getRefreshToken, saveTokens, getExpiresAt, getUserDiv, removeTokens } from 'utils/token-utils';
import { endpoints } from './endpoints';
import Paths from 'routes';
import { SERVER_URL } from 'commons/constants';

interface RequestProps {
  multiPart?: boolean;
  noAuth?: boolean;
  content_type?: string;
}

let isRefreshing = false;
let refreshSubscribers: any[] = [];

const onTokenRefreshed = (accessToken: string) => {
  refreshSubscribers.map((callback) => callback(accessToken));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback: any) => {
  refreshSubscribers.push(callback);
};

async function call(axiosConfig: AxiosRequestConfig, requestProp?: RequestProps | undefined) {
  if (!requestProp?.noAuth) {
    const token = localStorage.getItem('hsgaccessToken');
    axios.defaults.headers.common.token = token;
  } else {
    delete axios.defaults.headers.common.token;
  }

  if (requestProp?.content_type) {
    axios.defaults.headers['Content-Type'] = requestProp?.content_type;
  } else if (requestProp?.multiPart) {
    axios.defaults.headers['Content-Type'] = 'multipart/form-data';
  } else {
    axios.defaults.headers['Content-Type'] = 'application/x-www-form-urlencoded';
  }

  try {
    const { data, status }: AxiosResponse = await axios(axiosConfig);

    if (status !== 200) {
      return Promise.reject({ status, data });
    }

    return Promise.resolve(data);
  } catch (error: any) {
    return Promise.reject({ error: error || 'Something bad happened' });
  }
}

async function request(axiosConfig: AxiosRequestConfig, requestProp?: RequestProps | undefined): Promise<any> {
  axiosConfig.baseURL = SERVER_URL;

  if (!axiosConfig.url) {
    return Promise.reject();
  }

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const {
        config,
        response: { status, data },
      } = error;
      const originalRequest = config;

      if (status === 401) {
        if (data.code === 444 && moment(getExpiresAt()) < moment()) {
          removeTokens();
          location.href = Paths.pathObject.login;
        } else {
          const retryOriginalRequest = new Promise((resolve) => {
            addRefreshSubscriber((accessToken: string) => {
              originalRequest.headers.token = accessToken;
              resolve(axios(originalRequest));
            });
          });
          if (!isRefreshing) {
            isRefreshing = true;
            const response: AxiosResponse<TokenResDto> = await axios({
              baseURL: SERVER_URL,
              method: 'POST',
              url: endpoints.auth.refresh,
              data: paramsToFormData({ refreshToken: getRefreshToken() }),
            });
            if (response?.data?.result && response?.data.code === 200) {
              const { accessToken, refreshToken, userdiv } = response.data.result;
              saveTokens(accessToken, refreshToken?.refreshToken, refreshToken?.refreshTokenExpirationAt, userdiv);
              axios.defaults.headers.common.token = accessToken;
              isRefreshing = false;
              onTokenRefreshed(accessToken || '');
            } else if (response?.data.code === 445) {
              removeTokens();
              location.href = Paths.pathObject.login + '?error=loginDupl';
            }
          }

          return retryOriginalRequest;
        }
      }
      return Promise.reject(error);
    },
  );

  const data = await call(axiosConfig, requestProp);

  return Promise.resolve(data);
}

export default request;
